/**
 * Carbon skeleton text: 12px placeholder bars in the hover layer that pulse
 * while data loads (still under prefers-reduced-motion). Decorative unless
 * given a `label`, which makes it a polite status a screen reader announces.
 * DataTable draws its loading rows with it.
 */
import clsx from 'clsx'

export interface SkeletonProps {
  /** Number of bars. The last of several runs shorter, like a paragraph. */
  lines?: number
  /** Width of a single bar (any CSS length). Several bars vary on their own. */
  width?: string
  /** Announced text ("Loading drifts"); omit when the context already says it. */
  label?: string
  className?: string
}

const WIDTHS = ['100%', '92%', '84%']

export function Skeleton({ lines = 1, width, label, className }: SkeletonProps) {
  const bars = (
    <span aria-hidden="true" className={clsx('flex min-w-0 flex-col gap-2', !label && className)}>
      {Array.from({ length: Math.max(1, lines) }, (_, i) => (
        <span
          key={i}
          className="block h-3 animate-pulse bg-layer-hover"
          style={{ width: lines === 1 ? width ?? '100%' : i === lines - 1 ? '60%' : WIDTHS[i % WIDTHS.length] }}
        />
      ))}
    </span>
  )
  if (!label) return bars
  return (
    <span role="status" className={clsx('block min-w-0', className)}>
      {bars}
      <span className="sr-only">{label}</span>
    </span>
  )
}

export default Skeleton
